import {
  isMarkActive,
  someNode,
  toggleMark,
  toggleNodeType,
} from "@udecode/plate-core";
import { moveListItems, toggleList } from "@udecode/plate-list";

import {
  INTENT_TYPE_BEHAVIOR,
  INTENT_TYPE_BLOCK,
  INTENT_TYPE_MARK,
  ELEMENT_BLOCKQUOTE,
  ELEMENT_OL,
  ELEMENT_UL,
  INTENT_BULLETED_LIST,
  INTENT_NUMBERED_LIST,
  INTENT_QUOTE,
  INTENT_REDO,
  INTENT_UNDO,
  INTENT_INDENT,
  INTENT_OUTDENT,
  INTENT_HIDE_KEYBOARD,
} from "./constants";

import type { PlateEditor } from "@udecode/plate-core";
import type { Editor } from "./types";
import type {
  TextEditorControlBehaviorIntent,
  TextEditorControlBlockIntent,
  TextEditorControlMarkIntent,
  TextEditorControlIntent,
} from "./TextEditorControl";

/**
 * Finds the `contenteditable` node in the TextEditor.
 */
export function findTextEditorNode(root = document) {
  try {
    return root.querySelector<HTMLElement>(".TextEditor [contenteditable]");
  } catch (error) {
    return null;
  }
}

/**
 * Determines the `type` of intent becuase intents are handled differently.
 */
export function getIntentType(intent: TextEditorControlIntent) {
  switch (intent) {
    case INTENT_REDO:
    case INTENT_UNDO:
    case INTENT_INDENT:
    case INTENT_OUTDENT:
    case INTENT_HIDE_KEYBOARD:
      return INTENT_TYPE_BEHAVIOR;
    case INTENT_BULLETED_LIST:
    case INTENT_NUMBERED_LIST:
    case INTENT_QUOTE:
      return INTENT_TYPE_BLOCK;
    default:
      return INTENT_TYPE_MARK;
  }
}

export function getIntentNodeType(intent: TextEditorControlIntent) {
  const records = {
    [INTENT_BULLETED_LIST]: ELEMENT_UL,
    [INTENT_NUMBERED_LIST]: ELEMENT_OL,
    [INTENT_QUOTE]: ELEMENT_BLOCKQUOTE,
  } as Record<string, string>;

  return records[intent] ?? intent;
}

/**
 * Determines if a `block` intent is active.
 */
export function isBlockActive(
  editor: Editor,
  intent: TextEditorControlBlockIntent,
) {
  return (
    !!editor?.selection &&
    someNode(editor, { match: { type: getIntentNodeType(intent) } })
  );
}

/**
 * Determines if an intent is active, regardless of `type`.
 */
export function isIntentActive(
  editor: Editor,
  intent: TextEditorControlIntent,
) {
  const type = getIntentType(intent);

  switch (type) {
    case INTENT_TYPE_MARK:
      return isMarkActive(editor, intent as TextEditorControlMarkIntent);
    case INTENT_TYPE_BLOCK:
      return isBlockActive(editor, intent as TextEditorControlBlockIntent);
    default:
      return false;
  }
}

/**
 * Determines if an intent is disabled, regardless of `type`.
 */
export function isIntentDisabled(
  editor: Editor,
  intent: TextEditorControlIntent,
) {
  switch (intent) {
    case INTENT_INDENT:
    case INTENT_OUTDENT:
      return ![INTENT_BULLETED_LIST, INTENT_NUMBERED_LIST].some((i) =>
        isIntentActive(editor, i as TextEditorControlIntent),
      );
    case INTENT_UNDO:
      return !editor?.history.undos.length;
    case INTENT_REDO:
      return !editor?.history.redos.length;
    case INTENT_QUOTE:
      return [INTENT_BULLETED_LIST, INTENT_NUMBERED_LIST].some((i) =>
        isIntentActive(editor, i as TextEditorControlIntent),
      );
    default:
      return false;
  }
}

/**
 * Toggles a `block` intent on/off by updating the node data.
 */
export function toggleBlock(
  editor: Editor,
  intent: TextEditorControlBlockIntent,
) {
  switch (intent) {
    case INTENT_BULLETED_LIST:
      toggleList(editor, { type: ELEMENT_UL });
      break;
    case INTENT_NUMBERED_LIST:
      toggleList(editor, { type: ELEMENT_OL });
      break;
    case INTENT_QUOTE:
      toggleNodeType(editor, { activeType: ELEMENT_BLOCKQUOTE });
      break;
    default:
      break;
  }
}

/**
 * Performs a `behavior` intent.
 */
export function doBehavior(
  editor: Editor,
  intent: TextEditorControlBehaviorIntent,
) {
  switch (intent) {
    case INTENT_UNDO:
      editor.undo();
      break;
    case INTENT_REDO:
      editor.redo();
      break;
    case INTENT_INDENT:
      moveListItems(editor, { increase: true });
      break;
    case INTENT_OUTDENT:
      moveListItems(editor, { increase: false });
      break;
    default:
      break;
  }
}

/**
 * Performs the intent by routing to the approriate handler.
 */
export function doIntent(editor: Editor, intent: TextEditorControlIntent) {
  const type = getIntentType(intent);

  switch (type) {
    case INTENT_TYPE_MARK:
      toggleMark(editor, { key: intent as TextEditorControlMarkIntent });
      break;
    case INTENT_TYPE_BLOCK:
      toggleBlock(editor, intent as TextEditorControlBlockIntent);
      break;
    case INTENT_TYPE_BEHAVIOR:
      doBehavior(editor, intent as TextEditorControlBehaviorIntent);
      break;
    default:
      break;
  }
}

export function resetEditor(editor: Editor | PlateEditor) {
  const children = [...editor.children];

  children.forEach((node) =>
    editor.apply({ type: "remove_node", path: [0], node }),
  );
}
